import { Menu, MenuButton, MenuItem, MenuList, ColorModeProvider } from '@airbus/components-react';
import { KeyboardArrowDown, UnfoldMore } from '@airbus/icons/react';
import ProfileDialog from '../../../features/landing_page/ProfileDialog';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationsCount from './NotificationsCount';
import styles from './header.module.css';

export type HeaderMenuItem = {
  label: string;
  appName: string;
  appNameId: string | number;
  sortOrder?: number;
};
export type HeaderSection = { label: string; items: HeaderMenuItem[] };

type Props = {
  sections: HeaderSection[];
  notificationCount: number;
  setNotificationPanelOpen: (open: boolean) => void;
  loginUserId?: string;
  userName: string;
  userMenu: { name: string; fn: () => void }[];
  profileOpen: boolean;
  setProfileOpen: (v: boolean) => void;
  roleNames: any[];
  navigateTo: (appName: string, screenName: number | string) => void;
};

export default function HeaderDesktop({
  sections,
  notificationCount,
  setNotificationPanelOpen,
  loginUserId,
  userName,
  userMenu,
  profileOpen,
  setProfileOpen,
  roleNames,
  navigateTo,
}: Props) {
  const pathname = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
  const segments = new Set(pathname.split('/').filter(Boolean));

  const isSectionActive = (section: HeaderSection) =>
    section.items.some(({ appNameId, appName, label }) =>
      [appNameId, appName, label]
        .flatMap((v) => {
          const s = String(v ?? '').toLowerCase();
          return [s, s.replace(/[^a-z0-9]+/g, '-')];
        })
        .some((s) => s && segments.has(s)),
    );

  return (
    <>
      <ColorModeProvider mode='light'>
        <nav role='tablist' aria-label='Primary' className={styles.tabbar}>
          {sections.map((section) => {
            const active = isSectionActive(section);
            return (
              <Menu key={section.label}>
                <MenuButton
                  variant='Dark'
                  className={`${styles.tabButton} ${active ? styles.tabButtonActive : ''}`}
                  role='menu'
                  aria-selected={active}
                >
                  <span className={styles.tabLabel}>{section.label}</span>
                  <span className={styles.tabIcon}>
                    <UnfoldMore className={styles.tabIcon} />
                  </span>
                  <span
                    className={`${styles.tabUnderline} ${active ? styles.tabUnderlineActive : ''}`}
                  />
                </MenuButton>
                <MenuList className={styles.tabmenuList}>
                  {section.items.map((it) => (
                    <MenuItem
                      className={styles.tabmenuItem}
                      key={`${section.label}-${String(it.appNameId)}-${it.label}`}
                      onClick={() => navigateTo(it.appName, it.appNameId)}
                    >
                      {it.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            );
          })}
        </nav>

        {/* Right utilities */}
        {loginUserId && (
          <Menu>
            <MenuButton
              variant='ghostNegative'
              onClick={() => setNotificationPanelOpen(true)}
              className={styles.noright}
            >
              <NotificationsCount count={notificationCount} iconSize={20} />
            </MenuButton>
            <div className={styles.noright}>
              <LanguageSwitcher />
            </div>

            <>
              <MenuButton variant='ghostNegative'>
                {userName}
                <KeyboardArrowDown className={styles.arrowDownMenuIcon} />
              </MenuButton>

              <ProfileDialog open={profileOpen} setOpen={setProfileOpen} roleNames={roleNames} />
              <MenuList className={styles.tabmenuList}>
                {userMenu.map((m) => (
                  <MenuItem key={m.name} onClick={m.fn} className={styles.tabmenuItem}>
                    {m.name}
                  </MenuItem>
                ))}
              </MenuList>
            </>
          </Menu>
        )}
      </ColorModeProvider>
    </>
  );
}
